import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CopyButton } from "@/components/CopyButton";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";
import { MailOpen } from "lucide-react";

export default function Emails() {
  const [drafts, setDrafts] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = () => adminApi.get("/admin/email-drafts").then((r) => setDrafts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const markSent = async () => {
    try {
      await adminApi.post(`/admin/email-drafts/${selected.id}/mark-sent`);
      toast.success("Marked as manually sent");
      setSelected(null);
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <div className="max-w-6xl">
      <h1 className="font-heading font-bold text-2xl text-foreground mb-2" data-testid="admin-emails-title">Email drafts</h1>
      <p className="text-xs text-muted-foreground mb-6">
        Automated sending is <strong>disabled</strong> in Phase 1 (EMAIL_SENDING_ENABLED=false). Preview, copy, send
        manually from an authorized mailbox, then mark as sent — who and when is recorded.
      </p>

      <Card><CardContent className="p-0">
        <Table data-testid="email-drafts-table">
          <TableHeader>
            <TableRow>
              <TableHead>Template</TableHead>
              <TableHead>To</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {drafts.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground text-sm py-8">No drafts yet</TableCell></TableRow>
            )}
            {drafts.map((d) => (
              <TableRow key={d.id} className="cursor-pointer" onClick={() => setSelected(d)} data-testid={`email-draft-row-${d.id}`}>
                <TableCell><Badge variant="outline" className="text-xs">{d.template_key}</Badge></TableCell>
                <TableCell className="text-sm">{d.to}</TableCell>
                <TableCell className="text-sm max-w-xs truncate">{d.subject}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={d.status === "manually_sent" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-amber-50 text-amber-900 border-amber-200"}>
                    {d.status === "manually_sent" ? "sent manually" : "draft"}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono text-xs tabular-nums">{d.created_at?.slice(0, 16).replace("T", " ")}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2"><MailOpen className="h-5 w-5" /> {selected.subject}</DialogTitle>
              </DialogHeader>
              <div className="text-sm space-y-1">
                <p><span className="text-muted-foreground">From:</span> {selected.from}</p>
                <p><span className="text-muted-foreground">To:</span> {selected.to}</p>
                {selected.sent_marked_by && (
                  <p className="text-xs text-emerald-700">Marked sent by {selected.sent_marked_by} at {selected.sent_marked_at}</p>
                )}
              </div>
              <pre className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-xs whitespace-pre-wrap leading-relaxed max-h-80 overflow-y-auto" data-testid="email-draft-body">{selected.body}</pre>
              <div className="flex flex-wrap gap-2">
                <CopyButton text={selected.subject} label="Copy subject" testId="email-copy-subject" />
                <CopyButton text={selected.body} label="Copy body" testId="email-copy-body" />
                {selected.status !== "manually_sent" && (
                  <Button onClick={markSent} data-testid="email-mark-sent-button">Mark as manually sent</Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
